/* eslint-disable no-underscore-dangle */
import { DOCUMENT } from '@angular/common';
import { EventEmitter, inject, Injectable, signal, WritableSignal } from '@angular/core';

export interface ResizeHandleConfig {
  minWidth: number | string;
  maxWidth: number | string;
  position: 'start' | 'end';
  resizable: boolean;
  isMobile: boolean;
}

export interface ResizeHandleState {
  isResizing: boolean;
  startX: number;
  startWidth: number;
  currentWidth: string;
}

@Injectable({
  providedIn: 'root'
})
export class ResizeHandleService {
  private document = inject(DOCUMENT);
  private container: Element | null = null;
  private _resizeState: WritableSignal<ResizeHandleState> = signal({
    isResizing: false,
    startX: 0,
    startWidth: 0,
    currentWidth: '25rem'
  });

  resizeStart = new EventEmitter<MouseEvent | TouchEvent>();
  resizeMove = new EventEmitter<MouseEvent | TouchEvent>();
  resizeEnd = new EventEmitter<MouseEvent | TouchEvent>();

  get resizeState() {
    return this._resizeState.asReadonly();
  }

  getCurrentWidth() {
    return this._resizeState().currentWidth;
  }

  canResize(config: ResizeHandleConfig): boolean {
    return config.resizable || config.isMobile;
  }

  startResize(event: MouseEvent | TouchEvent, config: ResizeHandleConfig, cw: string): boolean {
    if (!this.canResize(config)) {
      return false;
    }

    event.preventDefault();

    const clientX = this.getClientX(event);
    const currentWidth = this.parseWidth(cw);

    this._resizeState.set({
      isResizing: true,
      startX: clientX,
      startWidth: currentWidth,
      currentWidth: this._resizeState().currentWidth
    });
    this.resizeStart.emit(event);
    return true;
  }

  moveResize(event: MouseEvent | TouchEvent, config: ResizeHandleConfig): string | null {
    if (!this._resizeState().isResizing) {
      return null;
    }

    event.preventDefault();

    const clientX = this.getClientX(event);
    const deltaX = config.position === 'end' ? this._resizeState().startX - clientX : clientX - this.resizeState().startX;

    const minWidth = this.parseConstraint(config.minWidth);
    const maxWidth = this.parseConstraint(config.maxWidth);

    const newWidth = Math.max(minWidth, Math.min(maxWidth, this._resizeState().startWidth + deltaX));
    const widthString = `${newWidth}px`;

    this._resizeState.update((state) => ({ ...state, currentWidth: widthString }));
    this.resizeMove.emit(event);
    return widthString;
  }

  endResize(event: MouseEvent | TouchEvent): void {
    if (!this._resizeState().isResizing) {
      return;
    }

    this._resizeState.update((state) => ({ ...state, isResizing: false }));
    this.resizeEnd.emit(event);
  }

  setWidth(width: string): void {
    this._resizeState.update((state) => ({ ...state, currentWidth: width }));
  }

  initializeWidth(initialWidth?: string): void {
    if (initialWidth) {
      this.setWidth(initialWidth);
    }
  }

  setContainer(container: Element): void {
    this.container = container;
  }

  private parseWidth(widthStr: string): number {
    if (widthStr.includes('px')) {
      return parseInt(widthStr.replace('px', ''));
    }
    if (widthStr.includes('rem')) {
      return parseFloat(widthStr.replace('rem', '')) * 16;
    }
    return 480;
  }

  private parseConstraint(constraint: number | string): number {
    if (typeof constraint === 'number') {
      return constraint;
    }
    if (constraint.includes('%')) {
      const containerWidth = this.container?.clientWidth || this.document.body.clientWidth;
      return (parseFloat(constraint.replace('%', '')) / 100) * containerWidth;
    }
    return 300;
  }

  private getClientX(event: MouseEvent | TouchEvent): number {
    if (event instanceof TouchEvent) {
      return event.touches[0]?.clientX || event.changedTouches[0]?.clientX || 0;
    }
    return event.clientX;
  }
}
